import {Loader} from '@mantine/core';
import {BasicButton, WhiteBgButtonThin} from '@packages/ui';
import {NotFoundError} from '@packages/utils';
import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import Image from 'next/image';
import React, {useEffect} from 'react';
import {FaTwitter} from 'react-icons/fa';
import {useInView} from 'react-intersection-observer';
import {z} from 'zod';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  BOX_PATH_ADVISERS_ME_EDIT,
  BOX_PATH_QUESTIONS_ADD,
  TWITTER_DOMAIN,
} from '../../../core/entities/box/constants';
import {
  getAdviser,
  useInfiniteAdviserQuestions,
} from '../../../core/repositories/boxRepository';
import type {
  AdviserResponse,
  QuestionWithAnswerType,
} from '../../../core/repositories/boxRepositoryTypes';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
  getUsersMe,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {AdviserQuestionItem} from '../../../features/box/components/AdviserQuestionItem';
import Layout from '../../../features/box/components/Layout';
import {Navigation} from '../../../features/box/components/Navigation';
import {createBoxPath} from '../../../features/box/usecases/routes';
import {withHandler} from '../../../server/token/withHandler';
import {CommonLink} from '../../../shared/components/CommonLink';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {convertNlToBr} from '../../../shared/services/convertNlToBr';
import {UnexpectedError} from '../../../utils/unexpectedError';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  loginAdviserName: string;
  adviser: AdviserResponse;
  isMe: boolean;
  metaInfo: {
    title: string;
    description: string;
    currentUrl: string;
  };
};

const QueryParameter = z.object({
  adviserName: z.string(),
});
type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps<Props> = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    const {adviserName} = context.validatedQuery;
    let loginAdviserName = '';

    const adviserResponse = await getAdviser({cookies, adviserName});
    if (!adviserResponse.ok) {
      if (adviserResponse.error === NotFoundError) {
        return {
          notFound: true,
        };
      }

      throw new Error(adviserResponse.error);
    }

    const currentUrl = `${GSSKT_HOST}/box/users/${adviserName}`;

    const props: Props = {
      adviser: adviserResponse.value,
      loginAdviserName,
      isMe: false,
      metaInfo: {
        title: `${adviserResponse.value.displayName || '相談室回答者'}さんの相談室${TITLE_SUFFIX}`,
        description: adviserResponse.value.introduction,
        currentUrl,
      },
    };

    if (!isValid) {
      return {
        props,
      };
    }

    const usersMeResponse = await getUsersMe({cookies});
    if (!usersMeResponse.ok) {
      throw new UnexpectedError();
    }

    loginAdviserName = usersMeResponse.value.adviserUserName;

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        loginAdviserName,
        isMe: adviserName === loginAdviserName,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const AdviserDetail: NextPageWithLayout<Props> = ({
  loginAdviserName,
  userDataForAnalytics,
  adviser,
  isMe,
  metaInfo,
}) => {
  const {ref, inView} = useInView();
  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteAdviserQuestions({
    adviserName: adviser.userName,
    limit: 10,
    hasAnswer: 1,
    questionOrderType: 'AnsweredAt',
  });

  useEffect(() => {
    void refetch();
  }, [adviser, refetch]);

  useEffect(() => {
    if (inView && hasNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const canEditProfile = Boolean(adviser) && isMe;

  return (
    <>
      <Head>
        <title key="title">{metaInfo.title}</title>
        <meta key="og:title" property="og:title" content={metaInfo.title} />
        <meta
          key="description"
          property="description"
          content={metaInfo.description}
        />
        <link rel="canonical" href={metaInfo.currentUrl} key="canonical" />
        <meta key="og:url" property="og:url" content={metaInfo.currentUrl} />
      </Head>
      <div className="flex min-h-screen justify-center bg-background-1 pb-40 pc:pt-[48px]">
        <div className="w-full max-w-[800px] bg-white pc:p-16">
          <>
            <div className="relative h-[139px] w-full bg-background-3">
              {Boolean(adviser.backgroundImageURL) && (
                <Image
                  src={adviser.backgroundImageURL}
                  style={{objectFit: 'cover'}}
                  fill
                  alt={adviser.displayName}
                />
              )}
              <div className="absolute bottom-[-44px] left-[50%] h-[150px] w-[150px] translate-x-[-50%] overflow-hidden rounded-full">
                <Image
                  src={
                    adviser.iconImageURL
                      ? adviser.iconImageURL
                      : '/next-image/box/default.svg'
                  }
                  style={{objectFit: 'cover'}}
                  fill
                  alt={
                    adviser.displayName ? adviser.displayName : '相談室回答者'
                  }
                />
              </div>
            </div>
            <div className="flex h-[44px] items-end justify-end px-12">
              {canEditProfile && (
                <CommonLink href={BOX_PATH_ADVISERS_ME_EDIT}>
                  <WhiteBgButtonThin
                    label="編集する"
                    className="w-[100px]"
                    isPrimary={false}
                  />
                </CommonLink>
              )}
            </div>
          </>

          <div className="mt-24 px-16">
            <div className="flex justify-center">
              <div
                className={`${
                  adviser.isStudent ? 'bg-category-7' : 'bg-category-3'
                } w-[45px] rounded-[20px]`}
              >
                <p className="text-center text-icon-sp text-white-1">
                  {adviser.isStudent ? '内定者' : '社会人'}
                </p>
              </div>
            </div>
            <div className="mt-8 flex justify-center">
              <h1 className="mr-8 text-lg font-bold leading text-black-1">
                {adviser.displayName ? adviser.displayName : '相談室回答者'}
              </h1>
              <CommonLink
                href={`${TWITTER_DOMAIN}/${adviser.userName}`}
                target="_blank"
                className="flex h-24 w-24 items-center justify-center rounded-full bg-sns-twitter pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
                rel="noreferrer"
              >
                <FaTwitter size={14} fill="#FFFFFF" />
              </CommonLink>
            </div>
            <div className="mt-16">
              <p className="text leading text-black-1">
                {convertNlToBr(adviser.introduction)}
              </p>
            </div>
            <div className="mt-8 bg-background-1 p-16">
              {Boolean(adviser.workingAt) && (
                <p className="flex items-center text leading text-black-1">
                  <span className="mr-8 block w-[42px] font-bold">勤務先</span>
                  <span className="block w-[calc(100%_-_50px)]">
                    {adviser.workingAt}
                  </span>
                </p>
              )}
              <p className="mt-8 flex items-center justify-between text leading text-black-1">
                <span className="mr-8 block w-[42px] font-bold">経歴等</span>
                <span className="block w-[calc(100%_-_50px)]">
                  {adviser.tags.map((tag, i) => {
                    return i < adviser.tags.length - 1
                      ? `${tag.name} / `
                      : tag.name;
                  })}
                </span>
              </p>
            </div>
            <div className="mt-16 flex justify-center">
              <CommonLink
                href={createBoxPath({
                  page: BOX_PATH_QUESTIONS_ADD,
                  adviserName: adviser.userName,
                })}
              >
                <BasicButton
                  label="この人に質問する"
                  className="w-[170px]"
                  isPrimary={false}
                  isLarge={false}
                  isDisabled={false}
                />
              </CommonLink>
            </div>
          </div>

          <div className="mt-[33px] bg-background-1 px-16 pt-24 pc:pb-24">
            <p className="text-md font-bold leading text-black-1">
              {adviser.displayName ? adviser.displayName : '相談室回答者'}
              さんの回答一覧
              <span className="ml-8 text-xs font">{adviser.answerCount}件</span>
            </p>
            <div className="mt-8">
              {data?.pages.flatMap((page) => {
                return (
                  page.ok && (
                    <React.Fragment key={`page-${page.value.nextNumber}`}>
                      {page.value.questions.map((q) => (
                        <div
                          ref={ref}
                          className="mb-12 last:mb-0"
                          key={keyOfAnswer(q)}
                        >
                          <AdviserQuestionItem question={q} adviser={adviser} />
                        </div>
                      ))}
                    </React.Fragment>
                  )
                );
              })}
              {!isFetching && (!data || data.pages.length <= 0) && (
                <div className=" px-16 py-32">
                  <p className=" text-center text-md leading text-black-1">
                    回答がありません
                  </p>
                </div>
              )}
              {(isFetching || isFetchingNextPage) && (
                <div className="flex justify-center py-24">
                  <Loader color="gray" size="lg" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Navigation loggedinAdviserName={loginAdviserName} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default AdviserDetail;

AdviserDetail.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

function keyOfAnswer(question: QuestionWithAnswerType) {
  return `key-${question.id}-${question.answers[0]?.id}`;
}
